/**
 * StudyFlowsTab — the study's automation flows (study-detail "Flows" tab).
 *
 * Lists the flows scoped to THIS study (trigger_config.study_slug = the study's
 * slug) and lets an admin spin up a new welcome flow in one click. Creating one
 * seeds a PHI-safe send-a-text template already scoped to the study (no slug to
 * type) and drops the admin into the FULL flow builder (/crm/settings/flows/:id) to edit
 * and publish. Editing/publishing live entirely in the builder — this tab is
 * the per-study entry point and roster.
 *
 * A study can keep the network-wide "generic" welcome AND/OR run its own flows.
 * When it runs its own, the "Skip the generic welcome for this study" toggle
 * (config.suppress_generic_welcome) stops the blanket welcome for this study's
 * leads so they don't get both — enforced server-side in enrollLeadInFlows.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, Plus, Workflow } from 'lucide-react';
import api from '../../../services/api';
import { CrmFlow, StudyDetailResponse } from '../../types';
import { relTime } from '../../lib';
import { Alert, Badge, type BadgeTone } from '../ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** A flow with no study_slug filter is network-wide — it enrolls EVERY lead,
 *  including this study's. An active one alongside a per-study flow double-texts
 *  unless this study opts out. Mirrors flowTargetsStudy's network-wide arm. */
function isNetworkWideLeadFlow(f: CrmFlow): boolean {
  if (f.trigger_type !== 'lead_created') return false;
  const s = f.trigger_config?.study_slug;
  return s == null || s === '' || (Array.isArray(s) && s.length === 0);
}

function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'active': return 'green';
    case 'archived': return 'gray';
    default: return 'amber';
  }
}

export default function StudyFlowsTab({
  detail, isAdmin, onChanged,
}: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const navigate = useNavigate();
  const studyId = detail.study.id;
  const slug = detail.study.slug;

  const [flows, setFlows] = useState<CrmFlow[]>([]);
  const [networkWide, setNetworkWide] = useState<CrmFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // The generic-welcome opt-out lives in studies.config; seed from the prop and
  // resync whenever the parent reloads the study (after a successful PATCH).
  const [suppress, setSuppress] = useState(detail.study.config?.suppress_generic_welcome === true);
  const [savingSuppress, setSavingSuppress] = useState(false);
  useEffect(() => {
    setSuppress(detail.study.config?.suppress_generic_welcome === true);
  }, [detail.study.config?.suppress_generic_welcome]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // This study's flows (any status) drive the list. In parallel, the active
      // flow set lets us flag if a network-wide flow would also text this
      // study's leads. The probe is best-effort — a failure must not break the list.
      const [scopedRes, activeRes] = await Promise.all([
        api.get('/api/crm/flows', { params: { study_slug: slug } }),
        api.get('/api/crm/flows', { params: { status: 'active' } }).catch(() => null),
      ]);
      setFlows(scopedRes.data?.flows || []);
      setNetworkWide(((activeRes?.data?.flows as CrmFlow[]) || []).filter(isNetworkWideLeadFlow));
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const createWelcome = async () => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api.post(`/api/crm/studies/${studyId}/flows`, {});
      // Hand off to the full builder, carrying the study id for a back-link.
      navigate(`/crm/settings/flows/${res.data.flow.id}?study=${studyId}`);
    } catch (e: any) {
      setError(errMsg(e));
      setCreating(false);
    }
  };

  const toggleSuppress = async (next: boolean) => {
    if (savingSuppress) return;
    const prev = suppress;
    setSuppress(next); // optimistic
    setSavingSuppress(true);
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${studyId}`, { suppress_generic_welcome: next });
      onChanged(); // reload the study so config (and other tabs) stay in sync
    } catch (e: any) {
      setSuppress(prev); // revert
      setError(errMsg(e));
    } finally {
      setSavingSuppress(false);
    }
  };

  const hasActive = flows.some((f) => f.status === 'active' && f.version > 0);
  const hasGeneric = networkWide.length > 0;
  const genericNames = networkWide.map((f) => `“${f.name}”`).join(', ');

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <Workflow className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Automation flows
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Every <strong>active</strong> flow below automatically texts (and follows up with) each new lead for this
            study. Build the message and cadence in the flow builder, then publish to go live. Use merge fields like{' '}
            <code className="rounded bg-gray-100 px-1 text-[11px] dark:bg-gray-700">{'{{first_name}}'}</code> and{' '}
            <code className="rounded bg-gray-100 px-1 text-[11px] dark:bg-gray-700">{'{{study_title}}'}</code> to personalize.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={createWelcome}
            disabled={creating}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {creating ? 'Creating…' : 'New welcome flow'}
          </button>
        )}
      </div>

      {error && (
        <Alert
          tone="error"
          icon={false}
          actions={
            <button
              onClick={load}
              className="min-h-[44px] shrink-0 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
            >
              Retry
            </button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Generic-welcome opt-out — only relevant once this study has its own flow(s). */}
      {isAdmin && !loading && flows.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <label className="flex min-h-[44px] cursor-pointer items-start gap-2.5 sm:min-h-0">
            <input
              type="checkbox"
              checked={suppress}
              disabled={savingSuppress}
              onChange={(e) => toggleSuppress(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">
              Skip the generic welcome for this study
              {savingSuppress && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-primary-600" />}
              <span className="mt-0.5 block text-[11px] font-normal text-gray-500 dark:text-gray-400">
                When on, this study's leads only get the flows below — not the network-wide “New Lead Welcome”.
              </span>
            </span>
          </label>
          {suppress && !hasActive && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              No active flow here yet — with the generic welcome off, this study currently sends no welcome at all.
            </p>
          )}
        </div>
      )}

      {/* Double-send heads-up: a generic flow is live, this study runs its own, and it has NOT opted out. */}
      {!loading && hasGeneric && hasActive && !suppress && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Leads for this study get the network-wide welcome ({genericNames}) <strong>and</strong> this study's flow.
            Turn on <strong>Skip the generic welcome for this study</strong> above to send only this study's message.
          </span>
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          null
        ) : flows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No flows for this study yet.{' '}
              {isAdmin
                ? 'Create a welcome flow to text every new lead automatically, or rely on the network-wide welcome.'
                : 'A CRM admin can set one up.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {flows.map((f) => (
                <button
                  key={f.id}
                  onClick={() => navigate(`/crm/settings/flows/${f.id}?study=${studyId}`)}
                  className="flex min-h-[44px] w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:min-h-0"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{f.name}</span>
                      <Badge size="sm" tone={statusTone(f.status)} className="shrink-0">
                        {f.status}
                      </Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {f.version > 0 ? `v${f.version}` : 'never published'}
                      {` · updated ${relTime(f.updated_at)}`}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{f.counts?.active_enrollments ?? 0}</p>
                    <p className="text-[11px] text-gray-400">in flow now</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!loading && !error && flows.length > 0 && !hasActive && (
        <p className="mt-3 text-[11px] text-amber-600 dark:text-amber-400">
          No flow here is live yet — open one and click <strong>Publish &amp; activate</strong> so new leads start getting texts.
        </p>
      )}
    </div>
  );
}
